/**
 * @fileoverview 乗組員1人分の「資格・期限」パネル（人材カード・資格期限管理ページ 共用）
 * @description 海技免状・船員手帳・STCW・健康証明書などを種別ごとに並べ、
 *              追加／編集／更新登録／更新手続き中の切り替え／スキャン添付／履歴の表示を1か所で行う。
 *              判定（残日数の色）はサーバー（/api/crew-certificates）が種別マスタの閾値で決めたものを使う。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Circle, FileText, History, Pencil, Plus, RefreshCw, Trash2, Upload, X, Clock } from 'lucide-react';
import * as api from '../services/api';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3001/api';

// ── 共有型（ページ側でも使う） ──────────────────────────────────────────
export type CertLevel = 'none' | 'ok' | 'warn' | 'danger' | 'expired' | 'renewing' | 'missing';
export interface CertTypeRow {
  id: string; code: string | null; name: string; short_name: string | null; category: string;
  validity_months: number | null; warn_days: number; danger_days: number; notify_stages: string;
  notify_admin: number; notify_self: number; visible_to_self: number; visible_to_captain: number;
  required: number; allow_multiple: number; has_number: number; has_grade: number;
  color: string; description: string | null; display_order: number; is_active: number; is_system: number;
}
export interface CertRecord {
  id: string; user_id: string; type_id: string; type_name: string; type_code: string | null; type_color: string;
  grade: string | null; cert_no: string | null; issued_date: string | null; expiry_date: string | null;
  status: 'active' | 'renewing' | 'renewed' | 'revoked';
  renewal_note: string | null; attention: number; note: string | null;
  file_path: string | null; file_name: string | null; renewed_from_id: string | null;
  created_by: string | null; created_at: string; updated_at: string;
  level: CertLevel; days: number | null;
  notifications?: { stage: number; sent_at: string }[];
}

export const LEVEL_STYLE: Record<CertLevel, { bg: string; fg: string; border: string; label: string }> = {
  expired:  { bg: '#dc2626', fg: '#ffffff', border: '#dc2626', label: '期限切れ' },
  danger:   { bg: '#fee2e2', fg: '#b91c1c', border: '#fecaca', label: '警告' },
  warn:     { bg: '#fef3c7', fg: '#b45309', border: '#fde68a', label: '注意' },
  renewing: { bg: '#dbeafe', fg: '#1d4ed8', border: '#bfdbfe', label: '更新手続き中' },
  ok:       { bg: '#f0fdf4', fg: '#16a34a', border: '#bbf7d0', label: '有効' },
  none:     { bg: '#f1f5f9', fg: '#64748b', border: '#e2e8f0', label: '期限なし' },
  missing:  { bg: '#f1f5f9', fg: '#64748b', border: '#e2e8f0', label: '未登録' },
};
export const STATUS_LABEL: Record<CertRecord['status'], string> = { active: '有効', renewing: '更新手続き中', renewed: '更新済み（旧）', revoked: '失効・返納' };
export const CATEGORY_LABEL: Record<string, string> = { qualification: '免状・資格', document: '手帳・証明書', training: '講習・訓練', health: '健康', contract: '契約・その他期限', other: 'その他' };

export const fmtYmd = (v?: string | null): string => (v ? v.slice(0, 10).replace(/-/g, '/') : '-');

/** 残日数の言い方（「残12日」「35日超過」「本日」） */
export function daysText(days: number | null | undefined, level?: CertLevel): string {
  if (level === 'missing') return '未登録';
  if (days == null) return '';
  if (days < 0) return `${-days}日超過`;
  if (days === 0) return '本日まで';
  return `残${days}日`;
}

/** 期限ピル（色は判定レベル） */
export const CertLevelPill: React.FC<{ level: CertLevel; days?: number | null; date?: string | null; compact?: boolean; title?: string }> = ({ level, days, date, compact, title }) => {
  const s = LEVEL_STYLE[level];
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: compact ? '1px 7px' : '2px 9px', borderRadius: 999,
      fontSize: compact ? 10.5 : 11.5, fontWeight: 700, background: s.bg, color: s.fg, border: `1px solid ${s.border}`, whiteSpace: 'nowrap', lineHeight: 1.7,
    }}>
      {(level === 'expired' || level === 'danger') && <AlertTriangle size={11} />}
      {level === 'renewing' && <RefreshCw size={11} />}
      {level === 'missing' ? '未登録' : <>{date ? fmtYmd(date) : ''}{days != null && <span style={{ opacity: 0.85 }}>{compact ? '' : '・'}{daysText(days, level)}</span>}</>}
    </span>
  );
};

/** 認証付きヘッダー（ファイルの直接 fetch 用） */
export function certAuthHeaders(): Record<string, string> {
  const subdomain = (() => {
    const h = window.location.hostname;
    if (h !== 'localhost' && h !== '127.0.0.1') { const p = h.split('.'); if (p.length >= 3 && p[0]) return p[0]; }
    return (import.meta.env.VITE_COMPANY_SUBDOMAIN as string | undefined) ?? 'unison-tech';
  })();
  return { Authorization: `Bearer ${api.getToken() ?? ''}`, 'X-Company-Subdomain': subdomain, 'X-App-Type': 'admin' };
}

/** 認証付きで xlsx / 添付ファイルをダウンロードして開く */
export async function downloadWithAuth(path: string, filename: string, openInTab = false): Promise<void> {
  const r = await fetch(`${API_BASE}${path}`, { headers: certAuthHeaders() });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  if (openInTab) { window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60_000); return; }
  const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const errMsg = (e: unknown, fallback: string) => {
  const m = (e as any)?.message ?? (e as any)?.error?.message;
  return typeof m === 'string' && m ? m : fallback;
};

// ── 入力フォーム（追加・編集・更新登録 共用） ──────────────────────────────
type FormMode = 'add' | 'edit' | 'renew';
interface FormState { type_id: string; grade: string; cert_no: string; issued_date: string; expiry_date: string; status: CertRecord['status']; renewal_note: string; attention: boolean; note: string }

function addMonthsYmd(ymd: string, months: number): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  if (isNaN(d.getTime())) return '';
  const day = d.getUTCDate(); d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() + months);
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, last));
  return d.toISOString().slice(0, 10);
}

export const CertForm: React.FC<{
  mode: FormMode; types: CertTypeRow[]; initial: Partial<FormState>; lockType?: boolean;
  saving: boolean; onSubmit: (v: FormState) => void; onCancel: () => void; title?: string;
}> = ({ mode, types, initial, lockType, saving, onSubmit, onCancel, title }) => {
  const [v, setV] = useState<FormState>({
    type_id: initial.type_id ?? types[0]?.id ?? '', grade: initial.grade ?? '', cert_no: initial.cert_no ?? '',
    issued_date: initial.issued_date ?? '', expiry_date: initial.expiry_date ?? '', status: initial.status ?? 'active',
    renewal_note: initial.renewal_note ?? '', attention: !!initial.attention, note: initial.note ?? '',
  });
  const t = types.find(x => x.id === v.type_id);
  const set = (k: keyof FormState, val: string | boolean) => setV(p => ({ ...p, [k]: val }));
  // 発行日を入れたら、種別の有効期間で期限を自動計算（手で直せる）
  const onIssued = (issued: string) => {
    setV(p => {
      const next = { ...p, issued_date: issued };
      if (issued && t?.validity_months && (!p.expiry_date || mode !== 'edit')) next.expiry_date = addMonthsYmd(issued, t.validity_months);
      return next;
    });
  };
  const input: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', background: '#fff' };
  const label: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4, display: 'block' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 'min(560px, 100%)', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: t?.color ?? '#64748b' }} />
          <div style={{ fontWeight: 800, color: '#0f172a', fontSize: 15 }}>{title ?? (mode === 'add' ? '資格を追加' : mode === 'renew' ? '更新登録（新しい期限）' : '資格を編集')}</div>
          <button onClick={onCancel} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b' }}><X size={18} /></button>
        </div>
        <div style={{ padding: 18, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 14px' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={label}>種別</label>
            <select style={input} value={v.type_id} disabled={lockType} onChange={e => set('type_id', e.target.value)}>
              {types.map(x => <option key={x.id} value={x.id}>{x.name}{x.validity_months ? `（有効${x.validity_months % 12 === 0 ? `${x.validity_months / 12}年` : `${x.validity_months}ヶ月`}）` : ''}</option>)}
            </select>
            {t?.description && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{t.description}</div>}
          </div>
          {!!t?.has_grade && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={label}>種類・等級</label>
              <input style={input} list="cert-grade-suggest" value={v.grade} onChange={e => set('grade', e.target.value)} placeholder="例: 四級海技士（航海）" />
              <datalist id="cert-grade-suggest">
                {['一級海技士（航海）', '二級海技士（航海）', '三級海技士（航海）', '四級海技士（航海）', '五級海技士（航海）', '六級海技士（航海）',
                  '一級海技士（機関）', '二級海技士（機関）', '三級海技士（機関）', '四級海技士（機関）', '五級海技士（機関）', '六級海技士（機関）',
                  '一級小型船舶操縦士', '二級小型船舶操縦士', '特殊小型船舶操縦士'].map(g => <option key={g} value={g} />)}
              </datalist>
            </div>
          )}
          {!!t?.has_number && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={label}>番号</label>
              <input style={input} value={v.cert_no} onChange={e => set('cert_no', e.target.value)} placeholder={t?.code === 'seaman_book' ? '例: 高松 第12345-2号' : '例: 5400 1200 00521'} />
            </div>
          )}
          <div>
            <label style={label}>{t?.category === 'health' ? '受診日' : t?.category === 'training' ? '受講・修了日' : '発行・交付日'}</label>
            <input style={input} type="date" value={v.issued_date} onChange={e => onIssued(e.target.value)} />
          </div>
          <div>
            <label style={label}>有効期限 {t?.validity_months ? <span style={{ color: '#94a3b8', fontWeight: 500 }}>（発行日から自動計算・手直し可）</span> : ''}</label>
            <input style={input} type="date" value={v.expiry_date} onChange={e => set('expiry_date', e.target.value)} />
          </div>
          {mode === 'edit' && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={label}>状態</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {(['active', 'renewing', 'revoked'] as const).map(s => (
                  <button key={s} type="button" onClick={() => set('status', s)}
                    style={{ padding: '6px 12px', borderRadius: 8, border: `1.5px solid ${v.status === s ? '#0369a1' : '#e2e8f0'}`, background: v.status === s ? '#e0f2fe' : '#fff', color: v.status === s ? '#0369a1' : '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    {STATUS_LABEL[s]}
                  </button>
                ))}
              </div>
            </div>
          )}
          {(mode === 'edit' && v.status === 'renewing') && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={label}>更新手続きのメモ（申請日・講習の予約・受診予定など）</label>
              <input style={input} value={v.renewal_note} onChange={e => set('renewal_note', e.target.value)} placeholder="例: 更新講習 10/15 受講予定・申請書提出済み" />
            </div>
          )}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ ...label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={v.attention} onChange={e => set('attention', e.target.checked)} />
              要留意（{t?.category === 'health' ? '健診の所見・配乗上の配慮' : '条件・制限・特記事項'}がある）
            </label>
            <textarea style={{ ...input, minHeight: 64, resize: 'vertical' }} value={v.note} onChange={e => set('note', e.target.value)}
              placeholder={t?.category === 'health' ? '例: 血圧高め・再検査予定。夜間当直の連続を避ける' : '例: 限定条件あり／講習機関・備考'} />
            <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 3 }}>留意事項は一覧の備考・船別有効期限表の備考に出ます。本人には見せません。</div>
          </div>
        </div>
        <div style={{ padding: '12px 18px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#f8fafc' }}>
          <button className="btn-o" onClick={onCancel} disabled={saving}>キャンセル</button>
          <button className="btn-p" disabled={saving || !v.type_id} onClick={() => onSubmit(v)}>{saving ? '保存中…' : mode === 'renew' ? '更新登録する' : '保存'}</button>
        </div>
      </div>
    </div>
  );
};

// ── パネル本体 ─────────────────────────────────────────────────────────────
const CrewCertificatePanel: React.FC<{ userId: string; onChanged?: () => void; compact?: boolean }> = ({ userId, onChanged, compact }) => {
  const [rows, setRows] = useState<CertRecord[]>([]);
  const [types, setTypes] = useState<CertTypeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<{ mode: FormMode; target?: CertRecord; typeId?: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get<any>(`/crew-certificates/users/${userId}`);
      setRows((r.data ?? []) as CertRecord[]);
      setTypes((r.types ?? []) as CertTypeRow[]);
      setError(null);
    } catch (e) { setError(errMsg(e, '資格の読み込みに失敗しました')); }
    setLoading(false);
  }, [userId]);
  useEffect(() => { setLoading(true); load(); }, [load]);

  const current = useMemo(() => rows.filter(r => r.status === 'active' || r.status === 'renewing'), [rows]);
  const history = useMemo(() => rows.filter(r => r.status === 'renewed' || r.status === 'revoked'), [rows]);
  const missing = useMemo(() => types.filter(t => t.required && !current.some(c => c.type_id === t.id)), [types, current]);
  // 種別の表示順で並べる（同じ種別が複数なら期限順）
  const ordered = useMemo(() => {
    const order = new Map(types.map((t, i) => [t.id, i]));
    return [...current].sort((a, b) => (order.get(a.type_id) ?? 99) - (order.get(b.type_id) ?? 99) || (a.expiry_date ?? '9999').localeCompare(b.expiry_date ?? '9999'));
  }, [current, types]);

  const submit = async (v: FormState) => {
    if (!form) return;
    setSaving(true);
    try {
      const body = { type_id: v.type_id, grade: v.grade, cert_no: v.cert_no, issued_date: v.issued_date, expiry_date: v.expiry_date, status: v.status, renewal_note: v.renewal_note, attention: v.attention ? 1 : 0, note: v.note };
      if (form.mode === 'add') await api.post(`/crew-certificates/users/${userId}`, body);
      else if (form.mode === 'edit' && form.target) await api.put(`/crew-certificates/${form.target.id}`, body);
      else if (form.mode === 'renew' && form.target) await api.post(`/crew-certificates/${form.target.id}/renew`, body);
      setForm(null);
      await load(); onChanged?.();
    } catch (e) { alert(errMsg(e, '保存に失敗しました')); }
    setSaving(false);
  };

  const toggleRenewing = async (c: CertRecord) => {
    setBusyId(c.id);
    try {
      const next = c.status === 'renewing' ? 'active' : 'renewing';
      const note = next === 'renewing' ? (window.prompt('更新手続きのメモ（任意）。例: 講習を10/15に予約', c.renewal_note ?? '') ?? null) : null;
      if (next === 'renewing' && note === null) { setBusyId(null); return; }
      await api.put(`/crew-certificates/${c.id}`, { status: next, renewal_note: next === 'renewing' ? note : '' });
      await load(); onChanged?.();
    } catch (e) { alert(errMsg(e, '更新に失敗しました')); }
    setBusyId(null);
  };

  const remove = async (c: CertRecord) => {
    setBusyId(c.id);
    try { await api.del(`/crew-certificates/${c.id}`); setConfirmDel(null); await load(); onChanged?.(); }
    catch (e) { alert(errMsg(e, '削除に失敗しました')); }
    setBusyId(null);
  };

  const uploadFile = async (c: CertRecord, file: File) => {
    setBusyId(c.id);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await fetch(`${API_BASE}/crew-certificates/${c.id}/file`, { method: 'POST', headers: certAuthHeaders(), body: fd });
      if (!r.ok) throw new Error();
      await load();
    } catch { alert('アップロードに失敗しました（PDF・JPEG・PNG・WebP、20MBまで）'); }
    setBusyId(null);
  };
  const deleteFile = async (c: CertRecord) => {
    setBusyId(c.id);
    try { const r = await fetch(`${API_BASE}/crew-certificates/${c.id}/file`, { method: 'DELETE', headers: certAuthHeaders() }); if (!r.ok) throw new Error(); await load(); }
    catch { alert('削除に失敗しました'); }
    setBusyId(null);
  };

  const smallBtn: React.CSSProperties = { fontSize: 11, borderRadius: 6, padding: '4px 9px', cursor: 'pointer', border: '1px solid #e2e8f0', background: '#fff', color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' };

  if (loading) return <div style={{ padding: 16, color: '#94a3b8', fontSize: 13 }}>読み込み中…</div>;
  if (error) return <div style={{ padding: 16, color: '#dc2626', fontSize: 13 }}>{error}</div>;

  const renderCard = (c: CertRecord) => {
    const s = LEVEL_STYLE[c.level];
    const t = types.find(x => x.id === c.type_id);
    const canAddMore = !!t?.allow_multiple;
    return (
      <div key={c.id} style={{ background: '#fff', border: `1px solid ${c.level === 'expired' || c.level === 'danger' ? s.border : '#e2e8f0'}`, borderLeft: `4px solid ${c.type_color}`, borderRadius: 12, padding: compact ? '10px 12px' : '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8' }}>{c.type_name}</div>
            <div style={{ fontSize: 14.5, fontWeight: 800, color: '#0f172a' }}>{c.grade || (t?.has_grade ? <span style={{ color: '#94a3b8', fontWeight: 500 }}>種類未入力</span> : STATUS_LABEL[c.status] === '有効' ? '登録あり' : STATUS_LABEL[c.status])}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {c.cert_no && <span>番号: {c.cert_no}</span>}
              {c.issued_date && <span>{t?.category === 'health' ? '受診' : t?.category === 'training' ? '修了' : '発行'}: {fmtYmd(c.issued_date)}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <CertLevelPill level={c.level} days={c.days} date={c.expiry_date} />
            {c.status === 'renewing' && c.renewal_note && <div style={{ fontSize: 11, color: '#1d4ed8', maxWidth: 240, textAlign: 'right' }}>{c.renewal_note}</div>}
          </div>
        </div>
        {!!c.attention && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 10px', fontSize: 12, color: '#78350f' }}>
            <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} /><span><b>要留意</b> {c.note || '（内容未記入）'}</span>
          </div>
        )}
        {!c.attention && c.note && <div style={{ fontSize: 12, color: '#64748b' }}>{c.note}</div>}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <button style={smallBtn} disabled={busyId === c.id} onClick={() => setForm({ mode: 'renew', target: c })}><RefreshCw size={11} />更新登録</button>
          <button style={{ ...smallBtn, ...(c.status === 'renewing' ? { background: '#dbeafe', color: '#1d4ed8', borderColor: '#bfdbfe' } : {}) }} disabled={busyId === c.id} onClick={() => toggleRenewing(c)}>
            <Clock size={11} />{c.status === 'renewing' ? '手続き中を解除' : '更新手続き中にする'}
          </button>
          <button style={smallBtn} disabled={busyId === c.id} onClick={() => setForm({ mode: 'edit', target: c })}><Pencil size={11} />編集</button>
          {canAddMore && <button style={smallBtn} onClick={() => setForm({ mode: 'add', typeId: c.type_id })}><Plus size={11} />もう1枚追加</button>}
          <span style={{ flex: 1 }} />
          {c.file_path ? (
            <>
              <button style={{ ...smallBtn, color: '#0369a1' }} onClick={() => downloadWithAuth(`/crew-certificates/${c.id}/file`, c.file_name ?? 'file', true)}><FileText size={11} />{c.file_name ? (c.file_name.length > 18 ? c.file_name.slice(0, 16) + '…' : c.file_name) : 'スキャン'}</button>
              <button style={{ ...smallBtn, color: '#dc2626' }} disabled={busyId === c.id} onClick={() => deleteFile(c)} title="添付を削除"><Trash2 size={11} /></button>
            </>
          ) : (
            <label style={{ ...smallBtn, cursor: busyId === c.id ? 'wait' : 'pointer' }}>
              <Upload size={11} />スキャン添付
              <input type="file" accept=".pdf,image/*" style={{ display: 'none' }} disabled={busyId === c.id} onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(c, f); e.currentTarget.value = ''; }} />
            </label>
          )}
          {confirmDel === c.id ? (
            <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 11 }}>
              削除しますか？<button style={{ ...smallBtn, background: '#dc2626', color: '#fff', borderColor: '#dc2626' }} onClick={() => remove(c)}>削除</button><button style={smallBtn} onClick={() => setConfirmDel(null)}>やめる</button>
            </span>
          ) : <button style={{ ...smallBtn, color: '#94a3b8' }} onClick={() => setConfirmDel(c.id)} title="削除"><Trash2 size={11} /></button>}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, color: '#64748b' }}>有効 {current.length} 件{missing.length > 0 && <span style={{ color: '#b45309', fontWeight: 700 }}> ／ 未登録 {missing.length} 件</span>}</span>
        <span style={{ flex: 1 }} />
        <button className="btn-p" style={{ padding: '6px 12px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 5 }} onClick={() => setForm({ mode: 'add' })}><Plus size={13} />資格を追加</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: 10 }}>
        {ordered.map(renderCard)}
        {missing.map(t => (
          <div key={t.id} style={{ background: '#f8fafc', border: '1.5px dashed #cbd5e1', borderLeft: `4px solid ${t.color}`, borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Circle size={15} color="#cbd5e1" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8' }}>{t.name}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#64748b' }}>未登録（全乗組員に必要）</div>
            </div>
            <button style={smallBtn} onClick={() => setForm({ mode: 'add', typeId: t.id })}><Plus size={11} />登録</button>
          </div>
        ))}
        {ordered.length === 0 && missing.length === 0 && <div className="empty" style={{ padding: 24, gridColumn: '1 / -1' }}>資格が登録されていません。「資格を追加」から登録してください。</div>}
      </div>

      {history.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <button onClick={() => setShowHistory(v => !v)} style={{ ...smallBtn, background: 'transparent' }}><History size={11} />更新履歴 {history.length} 件を{showHistory ? '隠す' : '表示'}</button>
          {showHistory && (
            <div style={{ marginTop: 8, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
              <table style={{ fontSize: 12 }}>
                <thead><tr><th>種別</th><th>種類・番号</th><th>発行</th><th>有効期限</th><th>状態</th><th></th></tr></thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id}>
                      <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: h.type_color }} />{h.type_name}</span></td>
                      <td style={{ color: '#64748b' }}>{[h.grade, h.cert_no].filter(Boolean).join(' / ') || '-'}</td>
                      <td>{fmtYmd(h.issued_date)}</td>
                      <td>{fmtYmd(h.expiry_date)}</td>
                      <td><span style={{ fontSize: 11, color: '#64748b' }}>{STATUS_LABEL[h.status]}</span></td>
                      <td style={{ textAlign: 'right' }}>
                        {h.file_path && <button style={smallBtn} onClick={() => downloadWithAuth(`/crew-certificates/${h.id}/file`, h.file_name ?? 'file', true)}><FileText size={11} /></button>}
                        {confirmDel === h.id
                          ? <span style={{ display: 'inline-flex', gap: 4, marginLeft: 4 }}><button style={{ ...smallBtn, background: '#dc2626', color: '#fff', borderColor: '#dc2626' }} onClick={() => remove(h)}>削除</button><button style={smallBtn} onClick={() => setConfirmDel(null)}>やめる</button></span>
                          : <button style={{ ...smallBtn, color: '#94a3b8', marginLeft: 4 }} onClick={() => setConfirmDel(h.id)}><Trash2 size={11} /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {form && (
        <CertForm
          mode={form.mode}
          types={form.mode === 'add' ? types.filter(t => t.is_active) : types}
          lockType={form.mode !== 'add'}
          initial={form.mode === 'add'
            ? { type_id: form.typeId ?? types.find(t => t.is_active)?.id ?? '' }
            : form.mode === 'renew'
              ? { type_id: form.target!.type_id, grade: form.target!.grade ?? '', cert_no: form.target!.cert_no ?? '', issued_date: '', expiry_date: '', status: 'active', attention: false, note: '' }
              : { type_id: form.target!.type_id, grade: form.target!.grade ?? '', cert_no: form.target!.cert_no ?? '', issued_date: form.target!.issued_date ?? '', expiry_date: form.target!.expiry_date ?? '', status: form.target!.status, renewal_note: form.target!.renewal_note ?? '', attention: !!form.target!.attention, note: form.target!.note ?? '' }}
          saving={saving}
          onSubmit={submit}
          onCancel={() => setForm(null)}
          title={form.mode === 'renew' ? `更新登録：${form.target?.type_name ?? ''}（現在の期限 ${fmtYmd(form.target?.expiry_date)} → 新しい期限）` : undefined}
        />
      )}
    </div>
  );
};

export default CrewCertificatePanel;
